import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Building2, Users, MessageSquare, ShieldCheck, BarChart3, Activity,
  Megaphone, Crown, CheckCircle2, Sparkles, ArrowRight, Quote, Circle,
} from "lucide-react";
import SprintrollLogo from "@/components/SprintrollLogo";
import FAQSection from "@/components/landing/FAQSection";

const CONTENT = {
  es: {
    nav: { pricing: "Precios", faq: "FAQ", signIn: "Iniciar sesión", cta: "Empezar" },
    hero: {
      eyebrow: "Plan Corporativo",
      title: "Un espacio de trabajo con reglas claras para tu equipo.",
      subtitle: "Panel de admin, estadísticas de accesos, chat interno y facturación centralizada — con la privacidad de que el admin ve la actividad, nunca el contenido.",
      cta: "Contratar Corporativo",
      secondary: "Ver precios",
      pill1: "Mínimo 3 usuarios",
      pill2: "50 MB por miembro",
      pill3: "Chat en tiempo real",
    },
    orgSection: {
      eyebrow: "Panel de Organización",
      title: "Un lugar donde el admin ve la salud del equipo — sin espiar el contenido.",
      desc: "Métricas de uso, gestión de miembros, control de asientos y facturación, todo en una pantalla. Diseñado para líderes que quieren visibilidad sin invasión.",
      bullets: [
        "Estadísticas de accesos por miembro (últimos 7 y 30 días).",
        "Cantidad de tableros propios y compartidos por persona.",
        "Feed de actividad en tiempo real (últimas 24 horas), filtrable por miembro.",
        "Gestión de asientos con checkout PayPal integrado.",
      ],
    },
    chatSection: {
      eyebrow: "Chat de Equipo",
      title: "Menos herramientas dispersas, más contexto en un solo lugar.",
      desc: "Envía mensajes directos entre miembros, broadcasts del admin al equipo entero y responde al equipo Sprintroll — sin salir de la app.",
      bullets: [
        "WebSocket en tiempo real (menos de 200 ms de latencia).",
        "Broadcasts solo para admin — control total del canal principal.",
        "DMs privados entre miembros con contadores de no-leídos.",
        "Canal directo con el equipo Sprintroll para soporte.",
      ],
    },
    privacyTitle: "Privacidad by design",
    privacyDesc: "El admin ve MÉTRICAS. Nunca contenido. Nunca. Sprintroll respeta la propiedad intelectual de cada miembro.",
    privacyRules: [
      { yes: true,  label: "Cantidad de tableros, tarjetas y logins" },
      { yes: true,  label: "Actividad del equipo (creado / editado / movido)" },
      { yes: false, label: "Títulos o descripciones de tarjetas" },
      { yes: false, label: "Comentarios o contenido de checklists" },
      { yes: false, label: "Adjuntos o archivos privados" },
    ],
    testimonial: {
      quote: "Migramos a Sprintroll cuando pasamos de 4 a 18 personas. Lo que más me sorprendió fue no tener que sacrificar la privacidad de los equipos para tener visibilidad. Ahora sé quién trabaja en qué sin necesidad de leer sus tarjetas.",
      author: "Andrea Márquez",
      role: "COO — Estudio Kaikó (equipo de 22 personas)",
    },
    ctaFinal: {
      title: "¿Listo para dar el paso al plan Corporativo?",
      desc: "Empieza con 3 asientos y suma más cuando lo necesites — sin permanencia, cancela cuando quieras.",
      cta: "Comenzar ahora",
      secondary: "Hablar con ventas",
    },
    priceLine: "Desde $4 USD por usuario/mes · Mínimo 3 asientos",
  },
  en: {
    nav: { pricing: "Pricing", faq: "FAQ", signIn: "Sign in", cta: "Get started" },
    hero: {
      eyebrow: "Corporate Plan",
      title: "A workspace with clear rules for your team.",
      subtitle: "Admin panel, access analytics, in-org chat and centralized billing — with the peace of mind that the admin sees activity, never the content.",
      cta: "Get Corporate",
      secondary: "See pricing",
      pill1: "Min 3 users",
      pill2: "50 MB per member",
      pill3: "Real-time chat",
    },
    orgSection: {
      eyebrow: "Organization Panel",
      title: "One place where the admin can see the team's pulse — without peeking into content.",
      desc: "Usage metrics, member management, seat control and billing, all on a single screen. Designed for leaders who want visibility without invasion.",
      bullets: [
        "Per-member access stats (last 7 and 30 days).",
        "Owned vs. shared board count per person.",
        "Real-time activity feed (last 24 hours), filterable by member.",
        "Seat management with integrated PayPal checkout.",
      ],
    },
    chatSection: {
      eyebrow: "Team Chat",
      title: "Fewer scattered tools, more context in one place.",
      desc: "Direct messages between members, admin broadcasts and a private line to the Sprintroll team — without leaving the app.",
      bullets: [
        "Real-time WebSocket (< 200 ms latency).",
        "Broadcasts admin-only — full control of the main channel.",
        "Private DMs between members with unread counters.",
        "Direct channel with the Sprintroll team for support.",
      ],
    },
    privacyTitle: "Privacy by design",
    privacyDesc: "The admin sees METRICS. Never content. Never. Sprintroll respects every member's intellectual property.",
    privacyRules: [
      { yes: true,  label: "Board / card / login counts" },
      { yes: true,  label: "Team activity (created / updated / moved)" },
      { yes: false, label: "Card titles or descriptions" },
      { yes: false, label: "Comments or checklist content" },
      { yes: false, label: "Private attachments or files" },
    ],
    testimonial: {
      quote: "We moved to Sprintroll when we grew from 4 to 18 people. What surprised me most was not having to sacrifice team privacy to get visibility. I now know who is working on what without needing to read their cards.",
      author: "Andrea Márquez",
      role: "COO — Estudio Kaikó (22-person team)",
    },
    ctaFinal: {
      title: "Ready to jump to Corporate?",
      desc: "Start with 3 seats and add more when you need — no lock-in, cancel anytime.",
      cta: "Start now",
      secondary: "Talk to sales",
    },
    priceLine: "From $4 USD per user/month · Minimum 3 seats",
  },
};

export default function ForTeams() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = (i18n.language || "es").startsWith("en") ? "en" : "es";
  const t = CONTENT[lang];

  const goToAuth = () => navigate("/?corporate=1#pricing");
  const goToPricing = () => navigate("/#pricing");

  return (
    <div className="min-h-screen relative" data-testid="for-teams-page">
      {/* Ambient gradient */}
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,_rgba(37,99,235,0.10),_transparent_50%)]" />

      {/* NAV */}
      <nav className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
        <button onClick={() => navigate("/")} data-testid="for-teams-logo">
          <SprintrollLogo className="h-7 w-auto" />
        </button>
        <div className="flex items-center gap-6 text-sm">
          <button onClick={goToPricing} className="hover:text-blue-600 dark:hover:text-blue-300 transition" data-testid="for-teams-nav-pricing">
            {t.nav.pricing}
          </button>
          <button onClick={() => navigate("/")} className="hover:text-blue-600 dark:hover:text-blue-300 transition" data-testid="for-teams-nav-signin">
            {t.nav.signIn}
          </button>
          <button onClick={goToAuth} className="h-9 px-4 rounded-full bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition" data-testid="for-teams-nav-cta">
            {t.nav.cta}
          </button>
        </div>
      </nav>

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-6 pt-8 pb-20 md:pb-28 grid lg:grid-cols-[1.05fr_1fr] gap-12 items-center">
        <div>
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-blue-600 dark:text-blue-400">
            <Building2 className="h-3.5 w-3.5" /> {t.hero.eyebrow}
          </span>
          <h1 className="font-display text-4xl md:text-6xl font-semibold tracking-tight mt-3 leading-[1.03]" data-testid="for-teams-hero-title">
            {t.hero.title}
          </h1>
          <p className="text-muted-foreground mt-5 text-lg max-w-xl">{t.hero.subtitle}</p>

          <div className="mt-6 flex flex-wrap gap-2 text-xs">
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 px-3 py-1 font-medium">{t.hero.pill1}</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-blue-500/15 text-blue-700 dark:text-blue-300 px-3 py-1 font-medium">{t.hero.pill2}</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/15 text-violet-700 dark:text-violet-300 px-3 py-1 font-medium">{t.hero.pill3}</span>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={goToAuth} className="h-12 px-6 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-medium shadow-lg shadow-blue-500/30 transition inline-flex items-center gap-2" data-testid="for-teams-hero-cta">
              {t.hero.cta} <ArrowRight className="h-4 w-4" />
            </button>
            <button onClick={goToPricing} className="h-12 px-6 rounded-full border border-blue-500/30 hover:bg-blue-500/5 font-medium transition" data-testid="for-teams-hero-secondary">
              {t.hero.secondary}
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-4">{t.priceLine}</p>
        </div>

        {/* Hero mock: org panel snippet */}
        <MockOrgPanel />
      </section>

      {/* ORG SECTION */}
      <section className="border-t border-white/40 dark:border-white/5 py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <MockOrgPanelFull />
          </div>
          <div className="order-1 lg:order-2">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-blue-600 dark:text-blue-400">
              <BarChart3 className="h-3.5 w-3.5" /> {t.orgSection.eyebrow}
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-3 leading-tight">
              {t.orgSection.title}
            </h2>
            <p className="text-muted-foreground mt-4 text-base md:text-lg">{t.orgSection.desc}</p>
            <ul className="mt-6 space-y-3">
              {t.orgSection.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm">
                  <CheckCircle2 className="h-5 w-5 mt-0.5 text-emerald-500 flex-none" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CHAT SECTION */}
      <section className="border-t border-white/40 dark:border-white/5 py-20 md:py-28 bg-gradient-to-b from-transparent to-blue-500/[0.03]">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-blue-600 dark:text-blue-400">
              <MessageSquare className="h-3.5 w-3.5" /> {t.chatSection.eyebrow}
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-3 leading-tight">
              {t.chatSection.title}
            </h2>
            <p className="text-muted-foreground mt-4 text-base md:text-lg">{t.chatSection.desc}</p>
            <ul className="mt-6 space-y-3">
              {t.chatSection.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm">
                  <CheckCircle2 className="h-5 w-5 mt-0.5 text-emerald-500 flex-none" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <MockChat lang={lang} />
        </div>
      </section>

      {/* PRIVACY */}
      <section className="border-t border-white/40 dark:border-white/5 py-20 md:py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest">
            <ShieldCheck className="h-4 w-4" /> {t.privacyTitle}
          </div>
          <p className="font-display text-2xl md:text-3xl font-semibold mt-5 leading-snug">{t.privacyDesc}</p>
          <ul className="mt-10 grid sm:grid-cols-2 gap-3 text-left max-w-2xl mx-auto">
            {t.privacyRules.map((r, i) => (
              <li
                key={i}
                data-testid={`privacy-rule-${i}`}
                className={"flex items-center gap-3 rounded-2xl px-4 py-3 text-sm " + (r.yes ? "bg-emerald-500/10 border border-emerald-500/20" : "bg-rose-500/10 border border-rose-500/20")}
              >
                {r.yes ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-300 flex-none" />
                ) : (
                  <Circle className="h-4 w-4 text-rose-500 flex-none" />
                )}
                <span className={r.yes ? "" : "line-through opacity-70"}>{r.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="border-t border-white/40 dark:border-white/5 py-20 md:py-24">
        <div className="max-w-3xl mx-auto px-6" data-testid="for-teams-testimonial">
          <div className="glass-panel rounded-3xl p-8 md:p-12 relative overflow-hidden">
            <Quote className="absolute -top-2 -left-2 h-32 w-32 text-blue-500/10" strokeWidth={1} />
            <p className="font-display text-xl md:text-2xl leading-relaxed relative z-10 italic">
              “{t.testimonial.quote}”
            </p>
            <div className="mt-6 flex items-center gap-3 relative z-10">
              <div className="h-11 w-11 rounded-full bg-gradient-to-br from-blue-500 to-violet-600 grid place-items-center text-white font-semibold text-sm">
                AM
              </div>
              <div>
                <div className="font-semibold">{t.testimonial.author}</div>
                <div className="text-xs text-muted-foreground">{t.testimonial.role}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-20 md:py-24 border-t border-white/40 dark:border-white/5">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight leading-tight" data-testid="for-teams-cta-title">
            {t.ctaFinal.title}
          </h2>
          <p className="text-muted-foreground mt-4 text-lg">{t.ctaFinal.desc}</p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <button onClick={goToAuth} className="h-12 px-8 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-medium shadow-lg shadow-blue-500/30 transition inline-flex items-center gap-2" data-testid="for-teams-final-cta">
              {t.ctaFinal.cta} <ArrowRight className="h-4 w-4" />
            </button>
            <a href="mailto:hola@sprintroll.app" className="h-12 px-8 rounded-full border border-blue-500/30 hover:bg-blue-500/5 font-medium transition inline-flex items-center" data-testid="for-teams-sales-link">
              {t.ctaFinal.secondary}
            </a>
          </div>
          <p className="text-xs text-muted-foreground mt-6">{t.priceLine}</p>
        </div>
      </section>

      {/* Reuse existing FAQ */}
      <FAQSection onCTAClick={goToAuth} />
    </div>
  );
}


/* ────────────────────────── Mock components ────────────────────────── */
function MockOrgPanel() {
  return (
    <div className="glass-panel rounded-3xl p-6 shadow-2xl shadow-blue-500/10" data-testid="for-teams-mock-org">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-blue-600 dark:text-blue-300 font-semibold">Organización · Corporativo</p>
          <p className="font-display text-xl font-semibold mt-0.5">Estudio Kaikó</p>
        </div>
        <div className="flex gap-2">
          <div className="rounded-lg bg-blue-500/15 px-3 py-1.5">
            <div className="text-[10px] uppercase tracking-wider text-blue-700 dark:text-blue-300 opacity-80">Seats</div>
            <div className="font-semibold text-blue-700 dark:text-blue-300">18/22</div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2 mb-4">
        {[
          { label: "Miembros", v: "18", i: Users },
          { label: "Tableros",  v: "127", i: BarChart3 },
          { label: "Activos 7d", v: "16", i: CheckCircle2 },
          { label: "Logins 30d", v: "412", i: Activity },
        ].map((s) => {
          const Icon = s.i;
          return (
            <div key={s.label} className="rounded-xl bg-muted/40 p-2.5">
              <Icon className="h-3 w-3 text-blue-500" />
              <div className="font-display text-lg font-semibold mt-1 leading-none">{s.v}</div>
              <div className="text-[10px] text-muted-foreground">{s.label}</div>
            </div>
          );
        })}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2 flex items-center gap-1.5">
        <Activity className="h-3 w-3" /> Actividad reciente
      </div>
      <div className="space-y-2">
        {[
          { name: "María López",   ac: "creó una tarjeta",     h: "hace 12 min" },
          { name: "Andrea Márquez", ac: "movió una tarjeta",    h: "hace 34 min" },
          { name: "Diego Ferrer",   ac: "compartió un tablero", h: "hace 1 h" },
        ].map((row, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <div className="h-6 w-6 rounded-full bg-gradient-to-br from-blue-500 to-violet-600 grid place-items-center text-white font-semibold text-[9px]">
              {row.name.split(" ").map(w => w[0]).slice(0, 2).join("")}
            </div>
            <div className="flex-1 min-w-0">
              <span className="font-medium">{row.name}</span>
              <span className="text-muted-foreground"> {row.ac}</span>
            </div>
            <div className="text-[10px] text-muted-foreground">{row.h}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MockOrgPanelFull() {
  return (
    <div className="glass-panel rounded-3xl p-6 shadow-2xl shadow-blue-500/10">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4" />
          <p className="font-display font-semibold text-base">Miembros</p>
        </div>
        <button className="h-7 rounded-full bg-blue-600 text-white text-xs font-medium px-3">+ Agregar</button>
      </div>
      <div className="space-y-2">
        {[
          { n: "Andrea Márquez", e: "andrea@kaiko.mx", r: true, b: "9 propios · 3 compartidos",  l: "hace 3 min",  c: 87 },
          { n: "María López",    e: "maria@kaiko.mx",  r: false, b: "6 propios · 2 compartidos",  l: "hace 20 min", c: 42 },
          { n: "Diego Ferrer",   e: "diego@kaiko.mx",  r: false, b: "4 propios · 5 compartidos",  l: "hace 1 h",    c: 35 },
          { n: "Sofía Chen",     e: "sofia@kaiko.mx",  r: false, b: "3 propios · 1 compartido",   l: "hace 4 h",    c: 21 },
        ].map((m) => (
          <div key={m.e} className="grid grid-cols-[auto_1fr_auto] gap-3 items-center py-2 border-b border-white/5 last:border-b-0">
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-violet-600 grid place-items-center text-white text-[10px] font-semibold">
              {m.n.split(" ").map(w => w[0]).join("")}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium flex items-center gap-1.5">
                {m.n}
                {m.r && <Crown className="h-3 w-3 text-blue-500" />}
              </div>
              <div className="text-[11px] text-muted-foreground truncate">{m.b}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] text-muted-foreground">{m.l}</div>
              <div className="text-[11px] font-semibold">{m.c} logins</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MockChat({ lang }) {
  const l = lang === "en";
  const messages = [
    { name: "Andrea (Admin)", text: l ? "Team, quick reminder: sprint review at 4pm." : "Equipo, recordatorio: revisión de sprint a las 4pm.", me: false, bc: true },
    { name: "María López",    text: l ? "Ready, I'll bring the metrics." : "Listo, llevo las métricas actualizadas.", me: true, bc: false },
    { name: "Diego Ferrer",   text: l ? "Great, adding demo notes." : "Perfecto, sumo notas de demo.", me: false, bc: false },
  ];
  return (
    <div className="glass-panel rounded-3xl overflow-hidden shadow-2xl shadow-blue-500/10" data-testid="for-teams-mock-chat">
      <div className="grid grid-cols-[130px_1fr] h-[420px]">
        {/* Sidebar */}
        <aside className="border-r border-white/10 py-3 px-2 text-xs">
          <p className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold px-2 pb-2">Threads</p>
          <div className="bg-blue-500/10 border-l-2 border-l-blue-500 rounded-md px-2 py-1.5 flex items-center gap-1.5 mb-1">
            <Megaphone className="h-3 w-3 text-amber-500" />
            <span className="font-semibold text-[11px] truncate">Kaikó · Team</span>
          </div>
          <div className="px-2 py-1.5 flex items-center gap-1.5 text-[11px] hover:bg-muted/40 rounded-md">
            <Users className="h-3 w-3" /><span className="truncate">María López</span>
            <span className="ml-auto bg-blue-600 text-white text-[8px] rounded-full min-w-[14px] h-[14px] grid place-items-center font-bold">2</span>
          </div>
          <div className="px-2 py-1.5 flex items-center gap-1.5 text-[11px] hover:bg-muted/40 rounded-md">
            <Users className="h-3 w-3" /><span className="truncate">Diego Ferrer</span>
          </div>
          <div className="pt-3">
            <p className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold px-2 pb-1">Soporte</p>
            <div className="px-2 py-1.5 flex items-center gap-1.5 text-[11px] hover:bg-muted/40 rounded-md">
              <ShieldCheck className="h-3 w-3 text-red-500" /><span className="truncate">Sprintroll</span>
            </div>
          </div>
        </aside>
        {/* Messages */}
        <div className="flex flex-col">
          <div className="border-b border-white/10 px-4 py-2.5 flex items-center gap-2">
            <Megaphone className="h-4 w-4 text-amber-500" />
            <div>
              <div className="font-semibold text-sm">Kaikó · Team</div>
              <div className="text-[10px] text-muted-foreground">{l ? "Broadcast to entire team" : "Broadcast a todo el equipo"}</div>
            </div>
          </div>
          <div className="flex-1 p-3 space-y-2 overflow-hidden">
            {messages.map((m, i) => (
              <div key={i} className={"flex gap-2 items-end " + (m.me ? "flex-row-reverse" : "")}>
                <div className={"h-6 w-6 rounded-full grid place-items-center text-white text-[8px] font-semibold flex-none " + (m.bc ? "bg-amber-500" : "bg-gradient-to-br from-blue-500 to-violet-600")}>
                  {m.name.slice(0, 2).toUpperCase()}
                </div>
                <div className={"max-w-[70%] " + (m.me ? "text-right" : "")}>
                  <div className="text-[9px] text-muted-foreground mb-0.5">{m.name}</div>
                  <div className={"rounded-2xl px-3 py-1.5 text-xs whitespace-pre-wrap break-words " + (m.me ? "bg-blue-600 text-white rounded-br-sm inline-block" : m.bc ? "bg-amber-500/10 border border-amber-500/20 rounded-bl-sm" : "bg-muted/70 rounded-bl-sm inline-block")}>
                    {m.text}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-white/10 p-2 flex gap-1.5">
            <div className="flex-1 rounded-lg bg-muted/40 px-3 py-2 text-[10px] text-muted-foreground italic">
              {l ? "Write to your team…" : "Escribe a tu equipo…"}
            </div>
            <button className="rounded-lg bg-blue-600 text-white px-2.5" disabled aria-hidden>
              <Sparkles className="h-3 w-3" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
